import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Pickaxe, Brain, Target, Gem, Crown, Shield,
  Loader2, Play, Square, RefreshCw, FlaskConical,
  Activity, Zap, Dna, TrendingUp,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from '../config';

const REGIME_LABELS = {
  TREND_UP: { text: "text-emerald-400", label: "Trend Up" },
  TREND_DOWN: { text: "text-red-400", label: "Trend Down" },
  RANGE_LOW_VOL: { text: "text-blue-400", label: "Range LV" },
  RANGE_HIGH_VOL: { text: "text-orange-400", label: "Range HV" },
  BREAKOUT_EXPANSION: { text: "text-yellow-400", label: "Breakout" },
  PANIC_LIQUIDATION: { text: "text-rose-400", label: "Panic" },
  MEAN_REVERTIVE_CHOP: { text: "text-violet-400", label: "Mean Rev" },
};

const TIER_COLORS = {
  EMERALD: "text-emerald-400",
  DIAMOND: "text-violet-400",
  GOLD: "text-amber-400",
  SILVER: "text-zinc-300",
  CANDIDATE: "text-zinc-500",
};

/* ═══════════════════════════════════════════
   SECTION 1 — Mining Engine Status
   ═══════════════════════════════════════════ */
const MiningEngineStatus = ({ minerStatus, tuningSessions, registryStats }) => {
  const s = minerStatus || {};
  const sessions = Object.values(tuningSessions || {});
  const activeSessions = sessions.filter(t => t.status === "running");

  const totalCandidates = (s.cycles_completed || 0) * 50 + activeSessions.reduce((sum, t) => sum + (t.tested || 0), 0);
  const totalDiscoveries = (s.total_discoveries || 0) + sessions.reduce((sum, t) => sum + (t.emeralds_found || 0) + (t.diamonds_found || 0) + (t.golds_found || 0), 0);

  const isAlive = s.running || activeSessions.length > 0;

  // Mining speed: from backend or compute from tuning sessions
  const minerSpeed = s.tests_per_min || 0;
  const tuningSpeed = activeSessions.reduce((sum, t) => {
    if (t.elapsed_sec > 0 && t.tested > 0) return sum + (t.tested / (t.elapsed_sec / 60));
    return sum;
  }, 0);
  const totalSpeed = Math.round(minerSpeed + tuningSpeed);

  // Uptime formatting
  const uptimeSec = s.uptime_sec || 0;
  const uptimeStr = uptimeSec > 3600
    ? `${Math.floor(uptimeSec / 3600)}h ${Math.floor((uptimeSec % 3600) / 60)}m`
    : uptimeSec > 60
      ? `${Math.floor(uptimeSec / 60)}m`
      : `${uptimeSec}s`;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="mining-engine-status">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 flex items-center justify-center border ${isAlive ? "bg-emerald-500/10 border-emerald-700/30" : "bg-zinc-800/60 border-zinc-700/40"}`}>
            <Pickaxe className={`w-5 h-5 ${isAlive ? "text-emerald-400" : "text-zinc-600"}`} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-zinc-200">Mining Engine</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`flex h-2 w-2 rounded-full ${isAlive ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"}`} />
              <span className={`text-xs font-mono ${isAlive ? "text-emerald-400" : "text-zinc-500"}`}>
                {isAlive ? "ACTIVE" : "IDLE"}
              </span>
              {isAlive && uptimeSec > 0 && (
                <span className="text-[10px] text-zinc-600 font-mono ml-1">uptime {uptimeStr}</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatusMetric
            label="Mining Status"
            value={isAlive ? "ACTIVE" : "IDLE"}
            color={isAlive ? "text-emerald-400" : "text-zinc-500"}
          />
          <StatusMetric
            label="Candidates Tested"
            value={totalCandidates.toLocaleString()}
            color="text-zinc-200"
          />
          <StatusMetric
            label="Discoveries"
            value={totalDiscoveries.toLocaleString()}
            color="text-emerald-400"
          />
          <StatusMetric
            label="Mining Speed"
            value={isAlive ? `${totalSpeed}/min` : "—"}
            color={isAlive ? "text-amber-400" : "text-zinc-500"}
          />
          <StatusMetric
            label="Active Jobs"
            value={activeSessions.length + (s.running ? 1 : 0)}
            color={activeSessions.length > 0 ? "text-amber-400" : "text-zinc-500"}
          />
        </div>

        {/* Miner sub-stats */}
        {s.running && (
          <div className="flex items-center gap-4 mt-3 px-3 py-2 bg-zinc-800/30 text-[10px] text-zinc-500">
            <span>Cycles: <span className="text-zinc-200 font-bold">{s.cycles_completed || 0}</span></span>
            <span>Evolutions: <span className="text-cyan-400 font-bold">{s.total_evolutions || 0}</span></span>
            <span>Promotions: <span className="text-violet-400 font-bold">{s.total_promotions || 0}</span></span>
            <span>Demotions: <span className="text-red-400 font-bold">{s.total_demotions || 0}</span></span>
            <span className="ml-auto text-zinc-600">{s.current_activity || "idle"}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatusMetric = ({ label, value, color = "text-zinc-200" }) => (
  <div className="bg-zinc-800/40 border border-zinc-700/30 p-3">
    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-lg font-bold font-mono ${color}`}>{value}</p>
  </div>
);

/* ═══════════════════════════════════════════
   SECTION 2 — Current Mining Jobs
   ═══════════════════════════════════════════ */
const CurrentMiningJobs = ({ minerStatus, tuningSessions, onStartMiner, onStopMiner, onLaunchTune, onLaunchAll, onRefresh }) => {
  const s = minerStatus || {};
  const sessions = Object.values(tuningSessions || {});
  const activeSessions = sessions.filter(t => t.status === "running" || t.status === "starting");
  const completedSessions = sessions.filter(t => t.status === "complete");
  const pendingSessions = sessions.filter(t => t.status !== "running" && t.status !== "complete" && t.status !== "interrupted");

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="current-mining-jobs">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-amber-400" /> Current Mining Jobs
          </h2>
          <div className="flex gap-2">
            {!s.running ? (
              <Button size="sm" onClick={onStartMiner} className="bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="start-miner-btn">
                <Play className="w-3 h-3 mr-1" /> Start Miner
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={onStopMiner} className="border-red-700 text-red-400" data-testid="stop-miner-btn">
                <Square className="w-3 h-3 mr-1" /> Stop
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onRefresh} className="border-zinc-700 text-zinc-400" data-testid="refresh-jobs-btn">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Continuous Miner Job */}
        {s.running && (
          <div className="border border-emerald-700/30 bg-emerald-950/10 p-3 mb-3" data-testid="miner-job-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-bold text-zinc-200">Continuous Miner</span>
                <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <Badge className="text-[8px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">RUNNING</Badge>
            </div>
            <p className="text-[10px] text-zinc-500 ml-6">{s.current_activity || "Mining..."}</p>
            {s.log && s.log.length > 0 && (
              <div className="ml-6 mt-2 bg-black/20 p-2 max-h-16 overflow-y-auto">
                {s.log.slice(-3).map((entry, i) => (
                  <p key={i} className="text-[9px] font-mono text-zinc-600">{entry}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tuning Lab Sessions */}
        {activeSessions.map((session) => {
          const rl = REGIME_LABELS[session.target_regime] || { text: "text-zinc-400", label: session.target_regime };
          const pct = session.pct || 0;
          return (
            <div key={session.session_id} className="border border-amber-700/20 bg-amber-950/5 p-3 mb-3" data-testid={`job-card-${session.session_id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Target className={`w-4 h-4 ${rl.text}`} />
                  <span className={`text-xs font-bold ${rl.text}`}>{rl.label} Tuning</span>
                  <span className="text-[10px] text-zinc-500 font-mono">({session.family || "ATR/KC"})</span>
                </div>
                <Badge className="text-[8px] bg-amber-500/20 text-amber-300 border-amber-500/40">MINING</Badge>
              </div>
              <div className="mb-1 ml-6">
                <div className="h-2 bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-600 to-orange-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[9px] text-zinc-500 mt-0.5">
                  {session.progress?.slice(0, 60) || ""} ({pct.toFixed(0)}%)
                </p>
              </div>
              <div className="flex gap-4 ml-6 text-[10px] text-zinc-500">
                <span>EMERALDs: <span className="text-emerald-400 font-bold">{session.emeralds_found || 0}</span></span>
                <span>DIAMONDs: <span className="text-violet-400 font-bold">{session.diamonds_found || 0}</span></span>
                <span>GOLDs: <span className="text-amber-400 font-bold">{session.golds_found || 0}</span></span>
              </div>
            </div>
          );
        })}

        {/* Completed Sessions (collapsed) */}
        {completedSessions.length > 0 && (
          <details className="mt-2">
            <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">
              {completedSessions.length} completed session(s)
            </summary>
            {completedSessions.map(session => {
              const rl = REGIME_LABELS[session.target_regime] || { text: "text-zinc-400", label: session.target_regime };
              return (
                <div key={session.session_id} className="ml-2 mt-1 text-[10px] text-zinc-600 font-mono flex items-center gap-2">
                  <span className={rl.text}>{rl.label}</span>
                  <span>{session.emeralds_found || 0}E / {session.diamonds_found || 0}D / {session.golds_found || 0}G</span>
                  {session.elapsed_sec > 0 && <span className="text-zinc-700">{session.elapsed_sec}s</span>}
                </div>
              );
            })}
          </details>
        )}

        {/* No active jobs */}
        {!s.running && activeSessions.length === 0 && (
          <div className="text-center py-6">
            <p className="text-zinc-600 text-xs mb-3">No active mining jobs</p>
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => onLaunchTune("TREND_DOWN")} className="border-red-700 text-red-400" data-testid="tune-trend-down-btn">
                <Target className="w-3 h-3 mr-1" /> Trend Down
              </Button>
              <Button size="sm" variant="outline" onClick={() => onLaunchTune("BREAKOUT_EXPANSION")} className="border-yellow-700 text-yellow-400" data-testid="tune-breakout-btn">
                <Target className="w-3 h-3 mr-1" /> Breakout
              </Button>
              <Button size="sm" variant="outline" onClick={() => onLaunchTune("MEAN_REVERTIVE_CHOP")} className="border-violet-700 text-violet-400" data-testid="tune-mean-revert-btn">
                <Target className="w-3 h-3 mr-1" /> Mean Revert
              </Button>
              <Button size="sm" onClick={onLaunchAll} className="bg-amber-700 hover:bg-amber-600 text-white" data-testid="tune-all-btn">
                <Zap className="w-3 h-3 mr-1" /> Mine All Regimes
              </Button>
            </div>
          </div>
        )}

        {/* Quick-launch bar when jobs are running */}
        {(s.running || activeSessions.length > 0) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800/50">
            <span className="text-[10px] text-zinc-500 uppercase">Launch:</span>
            <Button size="sm" variant="outline" onClick={() => onLaunchTune("TREND_DOWN")} className="border-red-700/50 text-red-400 h-7 text-[10px]" data-testid="tune-trend-down-btn">
              Trend Down
            </Button>
            <Button size="sm" variant="outline" onClick={() => onLaunchTune("BREAKOUT_EXPANSION")} className="border-yellow-700/50 text-yellow-400 h-7 text-[10px]" data-testid="tune-breakout-btn">
              Breakout
            </Button>
            <Button size="sm" variant="outline" onClick={() => onLaunchTune("MEAN_REVERTIVE_CHOP")} className="border-violet-700/50 text-violet-400 h-7 text-[10px]" data-testid="tune-mean-revert-btn">
              Mean Revert
            </Button>
            <Button size="sm" onClick={onLaunchAll} className="bg-amber-700 hover:bg-amber-600 text-white h-7 text-[10px]" data-testid="tune-all-btn">
              <Zap className="w-3 h-3 mr-1" /> All
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   SECTION 3 — Newly Discovered Gems
   ═══════════════════════════════════════════ */
const NewlyDiscoveredGems = ({ strategies, loading }) => {
  // Show the top recent discoveries — EMERALDs and DIAMONDs first
  const gems = (strategies || []).slice(0, 15);

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="newly-discovered-gems">
      <CardContent className="p-5">
        <h2 className="text-sm font-bold text-zinc-200 flex items-center gap-2 mb-4">
          <Gem className="w-4 h-4 text-emerald-400" /> Newly Discovered Gems
        </h2>

        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading discoveries...
          </div>
        )}

        {!loading && gems.length === 0 && (
          <p className="text-zinc-600 text-xs text-center py-6">No discoveries yet — start a mining job above</p>
        )}

        {!loading && gems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {gems.map((s, i) => {
              const tc = TIER_COLORS[s.tier] || "text-zinc-500";
              const rl = REGIME_LABELS[s.regime] || { text: "text-zinc-500", label: s.regime || "?" };
              const borderColor = s.tier === "EMERALD" ? "border-emerald-700/30" :
                                  s.tier === "DIAMOND" ? "border-violet-700/30" :
                                  s.tier === "GOLD" ? "border-amber-700/30" : "border-zinc-700/30";
              const bgColor = s.tier === "EMERALD" ? "bg-emerald-950/10" :
                              s.tier === "DIAMOND" ? "bg-violet-950/10" :
                              s.tier === "GOLD" ? "bg-amber-950/10" : "bg-zinc-800/40";

              return (
                <div key={s.strategy_id || i} className={`border ${borderColor} ${bgColor} p-3`} data-testid={`gem-card-${s.strategy_id || i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-zinc-200 font-bold truncate max-w-[160px]">
                      {s.strategy_name || s.strategy_id}
                    </span>
                    <span className={`text-[10px] font-bold ${tc}`}>{s.tier}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Sharpe</span>
                      <span className={`font-mono ${s.sharpe >= 2.5 ? "text-violet-400" : s.sharpe >= 1.5 ? "text-amber-400" : "text-zinc-300"}`}>
                        {s.sharpe?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Regime</span>
                      <span className={`font-mono ${rl.text}`}>{rl.label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">APY</span>
                      <span className={`font-mono ${(s.apy_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {s.apy_pct?.toFixed(0) || "—"}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Asset</span>
                      <span className="font-mono text-zinc-300">{s.asset?.replace("USDT", "")}</span>
                    </div>
                  </div>
                  {s.consistency !== undefined && s.consistency !== null && (
                    <div className="mt-2 pt-2 border-t border-zinc-700/20 text-[10px] flex justify-between">
                      <span className="text-zinc-500">Consistency</span>
                      <span className={`font-mono ${s.consistency > 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {s.consistency > 0 ? "+" : ""}{s.consistency?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   MAIN GOLD MINING PAGE
   ═══════════════════════════════════════════ */
export default function GoldMiningPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [minerStatus, setMinerStatus] = useState(null);
  const [tuningSessions, setTuningSessions] = useState({});
  const [registryStats, setRegistryStats] = useState(null);
  const [recentGems, setRecentGems] = useState([]);
  const [gemsLoading, setGemsLoading] = useState(true);

  const fetchMiner = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/regime/miner/status`, { headers });
      setMinerStatus(r.data);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTuning = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/regime/tune/status`, { headers });
      setTuningSessions(r.data?.sessions || {});
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGems = useCallback(async () => {
    setGemsLoading(true);
    try {
      const [strats, stats] = await Promise.all([
        axios.get(`${API}/regime/registry?limit=15`, { headers }),
        axios.get(`${API}/regime/registry/stats`, { headers }),
      ]);
      setRecentGems(strats.data?.strategies || []);
      setRegistryStats(stats.data);
    } catch {}
    setGemsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = useCallback(() => {
    fetchMiner();
    fetchTuning();
    fetchGems();
  }, [fetchMiner, fetchTuning, fetchGems]);

  useEffect(() => {
    refreshAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active jobs
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMiner();
      fetchTuning();
    }, 8000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startMiner = async () => {
    try {
      await axios.post(`${API}/regime/miner/start`, {}, { headers });
      toast.success("Continuous Miner started!");
      fetchMiner();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  const stopMiner = async () => {
    try {
      await axios.post(`${API}/regime/miner/stop`, {}, { headers });
      toast.success("Miner stopped");
      fetchMiner();
    } catch { toast.error("Failed to stop"); }
  };

  const launchTune = async (regime) => {
    try {
      await axios.post(`${API}/regime/tune/${regime}`, {}, { headers });
      toast.success(`Tuning ${regime} launched`);
      fetchTuning();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const launchTuneAll = async () => {
    try {
      await axios.post(`${API}/regime/tune-all`, {}, { headers });
      toast.success("All tuning sessions launched!");
      fetchTuning();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="gold-mining-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Pickaxe className="w-6 h-6 text-amber-400" />
            Gold Mining
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Strategy discovery engine — what new edges are we finding?
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refreshAll} className="border-zinc-700 text-zinc-400" data-testid="refresh-all-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Section 1: Mining Engine Status */}
      <MiningEngineStatus
        minerStatus={minerStatus}
        tuningSessions={tuningSessions}
        registryStats={registryStats}
      />

      {/* Section 2: Current Mining Jobs */}
      <CurrentMiningJobs
        minerStatus={minerStatus}
        tuningSessions={tuningSessions}
        onStartMiner={startMiner}
        onStopMiner={stopMiner}
        onLaunchTune={launchTune}
        onLaunchAll={launchTuneAll}
        onRefresh={refreshAll}
      />

      {/* Section 3: Newly Discovered Gems */}
      <NewlyDiscoveredGems
        strategies={recentGems}
        loading={gemsLoading}
      />
    </div>
  );
}
